import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { HubHandlerSourceAngular } from '@models/system';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { SignalRService } from '@shared/services';
import { AiReasoningStep, AiStoreActions, initialState } from '@signal-store/models/ai-store.model';
import { AgentRunThreadMessage } from '@system/search/cipo-ai/cipo-ai.component';
import { AiChatContextSelection, CipoAiService } from '@system/search/cipo-ai/cipo-ai.service';
import { catchError, EMPTY, pipe, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserStore } from './user.store';

export const AiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ai'),
  withState(initialState),

  withComputed((store, userStore = inject(UserStore)) => ({
    isOpen: computed(() => userStore.cipoAI.assistantOpened()),
    canStopRequest: computed(() => store.loading() && !!store.thread()?.threadId),
    context: computed(() => ({
      moduleId: userStore.menu.activeMenuItem()?.moduleId ?? null,
      contractId: userStore.contract()?.id ?? null,
      entityInstanceId: store.activeEntityInstanceId(),
      projectFolderId: store.activeProjectFolderId(),
    })),
  })),

  withMethods(
    (
      store,
      cipoAiService = inject(CipoAiService),
      userStore = inject(UserStore),
      translateService = inject(TranslateService),
    ) => {
      const cancelRequest$ = new Subject<void>();
      let softTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
      let requestStartedAtMs = 0;
      let softTimeoutsShownCount = 0;
      let keepWaitingCount = 0;

      const setLoading = (loading: boolean) => updateState(store, AiStoreActions.SetLoading, { loading });

      const resolveTimeoutConfig = () => {
        const softTimeoutSeconds = environment.aiChatSoftTimeoutSeconds > 0 ? environment.aiChatSoftTimeoutSeconds : 30;
        const extensionTimeoutSeconds =
          environment.aiChatTimeoutExtensionSeconds > 0 ? environment.aiChatTimeoutExtensionSeconds : 30;
        return { softTimeoutSeconds, extensionTimeoutSeconds };
      };

      const getElapsedSeconds = (): number => {
        if (!requestStartedAtMs) {
          return 0;
        }
        return Math.max(1, Math.round((Date.now() - requestStartedAtMs) / 1000));
      };

      const clearTimers = () => {
        if (softTimeoutTimer !== null) {
          clearTimeout(softTimeoutTimer);
          softTimeoutTimer = null;
        }
      };

      const translateWithFallback = (key: string, params: Record<string, unknown>, fallback: string): string => {
        const translated = translateService.instant(key, params);
        return translated && translated !== key ? translated : fallback;
      };

      const sendSummaryTelemetry = (resolvedThreadId?: string) => {
        const threadId = resolvedThreadId || store.thread()?.threadId || undefined;
        const elapsedSeconds = getElapsedSeconds();
        const timeoutSeconds = store.softTimeoutSeconds();
        const hadSoftTimeout = softTimeoutsShownCount > 0 || keepWaitingCount > 0;

        cipoAiService
          .recordTelemetry({
            eventType: hadSoftTimeout ? 'completed_after_soft_timeout' : 'completed',
            threadId,
            elapsedSeconds,
            timeoutSeconds,
            ...(hadSoftTimeout
              ? {
                  softTimeoutsShownCount,
                  keepWaitingCount,
                }
              : {}),
          })
          .pipe(
            take(1),
            catchError(() => EMPTY),
          )
          .subscribe();

        softTimeoutsShownCount = 0;
        keepWaitingCount = 0;
      };

      /**
       * Attaches the reasoning/tools-used steps captured for the current turn to the
       * last assistant message returned by the API, so the trace stays associated with
       * that specific message instead of being overwritten on the next question.
       */
      const attachReasoningStepsToLastAssistantMessage = (
        messages: AgentRunThreadMessage[],
        reasoningSteps: AiReasoningStep[],
      ): AgentRunThreadMessage[] => {
        if (reasoningSteps.length === 0) return messages;

        const lastAssistantIndex = messages.map(m => m.role).lastIndexOf('assistant');
        if (lastAssistantIndex === -1) return messages;

        return messages.map((message, index) =>
          index === lastAssistantIndex ? { ...message, reasoningSteps } : message,
        );
      };

      const appendSystemOutcomeMessage = (text: string) => {
        const currentSteps = store.reasoningSteps();
        const assistantMessage: AgentRunThreadMessage = {
          role: 'assistant',
          createdAt: new Date().toISOString(),
          items: [{ text }],
          ...(currentSteps.length > 0 ? { reasoningSteps: currentSteps } : {}),
        };

        const currentThread = store.thread();
        if (!currentThread) {
          return {
            thread: null,
            lastReasoningSteps: currentSteps,
          };
        }

        return {
          thread: {
            ...currentThread,
            messages: [...(currentThread.messages ?? []), assistantMessage],
          },
          lastReasoningSteps: currentSteps,
        };
      };

      const scheduleSoftTimeout = (delaySeconds: number) => {
        clearTimers();
        softTimeoutTimer = setTimeout(() => {
          softTimeoutTimer = null;
          if (!store.loading()) {
            return;
          }

          softTimeoutsShownCount++;
          const elapsedSeconds = Math.max(getElapsedSeconds(), delaySeconds);
          updateState(store, AiStoreActions.SoftTimeoutReached, {
            softTimeoutPromptVisible: true,
            elapsedTimeoutSeconds: elapsedSeconds,
          });
        }, delaySeconds * 1000);
      };

      const startRequestTimers = () => {
        clearTimers();
        requestStartedAtMs = Date.now();
        softTimeoutsShownCount = 0;
        keepWaitingCount = 0;
        const { softTimeoutSeconds, extensionTimeoutSeconds } = resolveTimeoutConfig();

        updateState(store, AiStoreActions.SetLoading, {
          softTimeoutSeconds,
          extensionTimeoutSeconds,
          softTimeoutPromptVisible: false,
          elapsedTimeoutSeconds: 0,
        });

        scheduleSoftTimeout(softTimeoutSeconds);
      };

      const loadThreads = rxMethod<void>(
        pipe(
          tap(() => setLoading(true)),
          switchMap(() =>
            cipoAiService.getThreads().pipe(
              tap(threads =>
                updateState(store, AiStoreActions.LoadThreadsSuccess, { threads: threads ?? [], loading: false }),
              ),
              catchError(() => {
                setLoading(false);
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      return {
        /** Filters out non-meaningful ("thinking") steps from a message's reasoning trace. */
        meaningfulReasoningSteps(message: AgentRunThreadMessage): AiReasoningStep[] {
          return (message.reasoningSteps ?? []).filter(s => s.kind !== 'thinking');
        },

        updateSidebarWidth(sidebarWidth: number) {
          updateState(store, AiStoreActions.UpdateSidebarWidth, { sidebarWidth });
        },

        setActiveEntityInstanceId(activeEntityInstanceId: number | null) {
          updateState(store, AiStoreActions.SetActiveEntityInstanceId, {
            activeEntityInstanceId:
              activeEntityInstanceId && activeEntityInstanceId > 0 ? activeEntityInstanceId : null,
          });
        },

        setActiveProjectFolderId(activeProjectFolderId: number | null) {
          updateState(store, AiStoreActions.SetActiveProjectFolderId, {
            activeProjectFolderId: activeProjectFolderId && activeProjectFolderId > 0 ? activeProjectFolderId : null,
          });
        },

        toggleOpen() {
          userStore.setAssistantOpened(!store.isOpen());
        },

        loadThreads,

        selectThread: rxMethod<string>(
          pipe(
            tap(() => {
              clearTimers();
              cancelRequest$.next();
              updateState(store, AiStoreActions.SelectThread, {
                loading: true,
                viewMode: 'thread',
                thread: null,
                softTimeoutPromptVisible: false,
              });
            }),
            switchMap(threadId =>
              cipoAiService.getThread(threadId).pipe(
                tap(thread => updateState(store, AiStoreActions.SelectThreadSuccess, { thread, loading: false })),
                catchError(() => {
                  setLoading(false);
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        keepWaiting() {
          if (!store.loading()) {
            return;
          }

          keepWaitingCount++;
          const elapsedSeconds = getElapsedSeconds() || store.softTimeoutSeconds();
          const extensionSeconds = store.extensionTimeoutSeconds();

          updateState(store, AiStoreActions.KeepWaiting, {
            softTimeoutPromptVisible: false,
            elapsedTimeoutSeconds: elapsedSeconds,
          });

          scheduleSoftTimeout(extensionSeconds);
        },

        stopRequest() {
          if (!store.loading() || !store.thread()?.threadId) {
            return;
          }

          const elapsedSeconds = getElapsedSeconds();
          clearTimers();
          softTimeoutsShownCount = 0;
          keepWaitingCount = 0;
          cancelRequest$.next();

          const cancelledText = translateWithFallback(
            'CIPO_AI.CHAT.CANCELLED_BY_USER',
            { seconds: elapsedSeconds },
            `Cancelled by user after ${elapsedSeconds} seconds.`,
          );

          const outcome = appendSystemOutcomeMessage(cancelledText);
          updateState(store, AiStoreActions.StopRequest, {
            thread: outcome.thread,
            loading: false,
            softTimeoutPromptVisible: false,
            elapsedTimeoutSeconds: elapsedSeconds,
            reasoningSteps: [],
            lastReasoningSteps: outcome.lastReasoningSteps,
          });
        },

        askQuestion: rxMethod<{ text: string; contextSelection?: AiChatContextSelection }>(
          pipe(
            tap(({ text }) => {
              clearTimers();
              requestStartedAtMs = 0;
              softTimeoutsShownCount = 0;
              keepWaitingCount = 0;
              const { softTimeoutSeconds, extensionTimeoutSeconds } = resolveTimeoutConfig();

              const userMessage: AgentRunThreadMessage = {
                role: 'user',
                createdAt: new Date().toISOString(),
                items: [{ text }],
              };

              const currentThread = store.thread();
              if (currentThread) {
                updateState(store, AiStoreActions.AskQuestion, {
                  loading: true,
                  thread: { ...currentThread, messages: [...(currentThread.messages ?? []), userMessage] },
                  reasoningSteps: [],
                  lastReasoningSteps: [],
                  softTimeoutSeconds,
                  extensionTimeoutSeconds,
                  softTimeoutPromptVisible: false,
                  elapsedTimeoutSeconds: 0,
                });
                if (currentThread.threadId) {
                  startRequestTimers();
                }
              } else {
                updateState(store, AiStoreActions.AskQuestion, {
                  loading: true,
                  thread: { threadId: '', createdAt: new Date().toISOString(), messages: [userMessage] },
                  reasoningSteps: [],
                  lastReasoningSteps: [],
                  softTimeoutSeconds,
                  extensionTimeoutSeconds,
                  softTimeoutPromptVisible: false,
                  elapsedTimeoutSeconds: 0,
                });
              }
            }),
            switchMap(({ text, contextSelection }) => {
              const threadId = store.thread()?.threadId || undefined;
              const isNewThread = !threadId;

              return cipoAiService.askQuestion(threadId, text, contextSelection).pipe(
                takeUntil(cancelRequest$),
                tap(response => {
                  clearTimers();

                  if (!response) {
                    softTimeoutsShownCount = 0;
                    keepWaitingCount = 0;
                    updateState(store, AiStoreActions.SetLoading, {
                      loading: false,
                      softTimeoutPromptVisible: false,
                    });
                    return;
                  }

                  sendSummaryTelemetry(response.threadId);

                  const currentSteps = store.reasoningSteps();
                  const responseMessages = attachReasoningStepsToLastAssistantMessage(
                    response.messages ?? [],
                    currentSteps,
                  );

                  if (isNewThread) {
                    const userMessage: AgentRunThreadMessage = {
                      role: 'user',
                      createdAt: new Date().toISOString(),
                      items: [{ text }],
                    };
                    const messages = [userMessage, ...responseMessages];
                    updateState(store, AiStoreActions.AskQuestionSuccess, {
                      thread: { ...response, messages },
                      threads: [{ ...response, messages }, ...store.threads()],
                      loading: false,
                      softTimeoutPromptVisible: false,
                      lastReasoningSteps: currentSteps,
                      reasoningSteps: [],
                    });
                  } else {
                    updateState(store, AiStoreActions.AskQuestionSuccess, {
                      thread: {
                        ...response,
                        messages: [...(store.thread()?.messages ?? []), ...responseMessages],
                      },
                      loading: false,
                      softTimeoutPromptVisible: false,
                      lastReasoningSteps: currentSteps,
                      reasoningSteps: [],
                    });
                  }
                }),
                catchError(() => {
                  clearTimers();
                  softTimeoutsShownCount = 0;
                  keepWaitingCount = 0;
                  updateState(store, AiStoreActions.ClearReasoningSteps, {
                    loading: false,
                    softTimeoutPromptVisible: false,
                    reasoningSteps: [],
                    lastReasoningSteps: [],
                  });
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        newThread() {
          clearTimers();
          cancelRequest$.next();
          updateState(store, AiStoreActions.NewThread, {
            thread: null,
            loading: false,
            viewMode: 'thread',
            softTimeoutPromptVisible: false,
            reasoningSteps: [],
            lastReasoningSteps: [],
          });
        },

        onReasoningStepReceived(step: AiReasoningStep) {
          if (!store.loading()) {
            return;
          }

          const hadThreadId = !!store.thread()?.threadId;
          updateState(store, AiStoreActions.AddReasoningStep, state => ({
            reasoningSteps: [...state.reasoningSteps, step],
            thread:
              step.chatId && state.thread && !state.thread.threadId
                ? { ...state.thread, threadId: step.chatId }
                : state.thread,
          }));

          if (!hadThreadId && step.chatId) {
            startRequestTimers();
          }
        },

        setViewMode(viewMode: 'thread' | 'threads') {
          updateState(store, AiStoreActions.SetViewMode, { viewMode });
          if (viewMode === 'threads') {
            loadThreads();
          }
        },

        deleteThread: rxMethod<string>(
          pipe(
            tap(() => setLoading(true)),
            switchMap(threadId =>
              cipoAiService.deleteThread(threadId).pipe(
                tap(() =>
                  updateState(store, AiStoreActions.DeleteThreadSuccess, state => ({
                    loading: false,
                    threads: state.threads.filter(t => t.threadId !== threadId),
                    thread: state.thread?.threadId === threadId ? null : state.thread,
                  })),
                ),
                catchError(() => {
                  setLoading(false);
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };
    },
  ),

  withHooks((store, signalRService = inject(SignalRService)) => ({
    onInit: () => {
      signalRService.registerHandler(HubHandlerSourceAngular, 'AiReasoningStep', (step: AiReasoningStep) => {
        store.onReasoningStepReceived(step);
      });
    },
  })),
);
